// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Tile } from '@carbon/react';
import { usePlanDetailContext } from './PlanDetailContext';

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending Review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

function formatAttributeValue(value: unknown): string {
  if (value == null) return '—';
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function AuditEntry({
  step,
  title,
  timestamp,
  hasLine,
  children,
}: {
  step: number;
  title: string;
  timestamp?: string;
  hasLine?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="sched-audit-item">
      <div className="sched-audit-marker">
        <div className="sched-audit-circle">{step}</div>
        {hasLine && <div className="sched-audit-line" />}
      </div>
      <Tile className="sched-audit-card">
        <div className="sched-audit-card-head">
          <h4 className="sched-section-title">{title}</h4>
          {timestamp && <span className="sched-section-sub">{timestamp}</span>}
        </div>
        {children}
      </Tile>
    </div>
  );
}

function PlanAudit() {
  const { plan } = usePlanDetailContext();
  const reviews = plan.reviews;
  // The approval's own state is the final outcome, which may differ from any
  // single review once multiple reviewers are supported (e.g. quorum rules) —
  // so it gets its own block after the individual review entries, not folded
  // into the last review shown.
  const hasFinalDecision = plan.status === 'APPROVED' || plan.status === 'REJECTED';
  const totalSteps = 1 + reviews.length + (hasFinalDecision ? 1 : 0);

  return (
    <div className="sched-stack">
      <div className="sched-audit-timeline">
        <AuditEntry
          step={1}
          title="Plan Created"
          timestamp={plan.createdAt ? new Date(plan.createdAt).toLocaleString() : plan.date}
          hasLine={totalSteps > 1}
        >
          <div className="sched-kv">
            <div className="sched-kv-row">
              <span className="sched-kv-label">Plan Key</span>
              <span className="sched-kv-value sched-mono">{plan.storeKey}</span>
            </div>
            <div className="sched-kv-row">
              <span className="sched-kv-label">Site</span>
              <span className="sched-kv-value">{plan.dc_site_id}</span>
            </div>
            <div className="sched-kv-row">
              <span className="sched-kv-label">Status</span>
              <span className="sched-kv-value">{STATUS_LABELS[plan.status] ?? plan.status}</span>
            </div>
          </div>
        </AuditEntry>

        {reviews.map((review, i) => {
          const step = i + 2;
          return (
            <AuditEntry
              key={review.reviewId || i}
              step={step}
              title={`Review ${STATUS_LABELS[review.state] ?? review.state}`}
              // Reviews carry no timestamp of their own — `review_date` (the
              // approval's updatedAt) is the closest available signal, and only
              // meaningful once the review has actually been decided.
              timestamp={
                review.state !== 'PENDING' && plan.review_date
                  ? new Date(plan.review_date).toLocaleString()
                  : undefined
              }
              hasLine={step < totalSteps}
            >
              <div className="sched-kv">
                <div className="sched-kv-row">
                  <span className="sched-kv-label">Review ID</span>
                  <span className="sched-kv-value sched-mono">{review.reviewId}</span>
                </div>
                {Object.entries(review.attributes ?? {}).map(([key, value]) => (
                  <div className="sched-kv-row" key={key}>
                    <span className="sched-kv-label">{key}</span>
                    <span className="sched-kv-value">{formatAttributeValue(value)}</span>
                  </div>
                ))}
              </div>
            </AuditEntry>
          );
        })}

        {hasFinalDecision && (
          <AuditEntry
            step={totalSteps}
            title={`Plan ${STATUS_LABELS[plan.status] ?? plan.status}`}
            timestamp={plan.review_date ? new Date(plan.review_date).toLocaleString() : undefined}
          />
        )}
      </div>
    </div>
  );
}

export default PlanAudit;
